import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Paginator } from 'primereact/paginator';
import Sidebar from './sidebar/Sidebar';
import Breadcrumbs from './Breadcrumbs';
import Snackbar from '@mui/material/Snackbar';
import Loader from './Loader';
import apiService from '../utils/api';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import jspdfAutotable from 'jspdf-autotable';
import './Client.css';
import CircularProgress from '@mui/material/CircularProgress';

const Clients = () => {
  const [clients, setClients] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, success: false, message: '' });
  const [search, setSearch] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [first, setFirst] = useState(0);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [selectedDetails, setSelectedDetails] = useState(null);
  const [form, setForm] = useState({ name: '', code: '', email: '', partner_id: '' });
  const [partners, setPartners] = useState([]);
  const exportRef = useRef(null);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const mandatoryColumns = ['id', 'name', 'code'];     
  const [deleteModal, setDeleteModal] = useState({
  open: false,
  client: null,
  isDeleting: false,
  reason: ''
});          

const [visibleColumns, setVisibleColumns] = useState(() => {
  const saved = localStorage.getItem('clientTableVisibleColumns');
  const defaults = {
    id: true,
    name: true,
    code: true,
    email: true,
    created_at: true,
    actions: true,
  };
  const cols = saved ? JSON.parse(saved) : defaults;
  mandatoryColumns.forEach(c => (cols[c] = true));
  return cols;
});

const [tempVisibleColumns, setTempVisibleColumns] = useState(visibleColumns);
const [showColumnOptions, setShowColumnOptions] = useState(false);


  const getUserId = () => Number(sessionStorage.getItem('user_id')) || 0;
  const toggleColumn = (key) => {
  setTempVisibleColumns((prev) => ({
    ...prev,
    [key]: !prev[key],
  }));
};

const fetchClients = async () => {
  try {
    setLoading(true);
    const res = await apiService.fetchData('add_client');
    const results = 
      res?.clients?.results && Array.isArray(res.clients.results)
        ? res.clients.results
        : Array.isArray(res?.clients)
        ? res.clients
        : Array.isArray(res)
        ? res
        : res?.data && Array.isArray(res.data)
        ? res.data
        : [];

    const normalized = results.map((c, idx) => {
      const id = c.client_id || c.id || idx + 1;
      const name = c.clientName || c.client_name || c.name || 'N/A';
      const code = c.client_code || c.clientCode || c.code || 'N/A';
      let email = c.clientEmail || c.client_email || c.email || '';
      // Backend currently stores code into clientEmail; hide it in UI
      if (email && code && String(email).trim() === String(code).trim()) {
        email = '';
      }
      return { id, name, code, email: email || 'N/A', created_at: formatDateYMD(c.created_at) || ''  };
    });

    setClients(normalized);
    setFiltered(normalized);
  } catch (e) {
    console.error('Error fetching clients:', e);
    setSnackbar({ open: true, success: false, message: 'Failed to load clients' });
  } finally {
    setLoading(false);
  }
};

  useEffect(() => { fetchClients(); }, []);

  useEffect(() => {
    (async () => {
      try {
        const res = await apiService.fetchData('user/all');
        const list = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
        const options = list.map(u => ({ id: u.user_id, name: u.user_name }));
        setPartners(options);
      } catch (e) {
        setPartners([]);
      }
    })();
  }, []);
  

  useEffect(() => {
    const q = search.toLowerCase();
    setFiltered(
      clients.filter(c => `${c.name} ${c.code} ${c.email} ${c.id}`.toLowerCase().includes(q))
    );
    setCurrentPage(1);
  }, [search, clients]);

  useEffect(() => {
  if (showColumnOptions) {
    setTempVisibleColumns(visibleColumns);
  }
}, [showColumnOptions, visibleColumns]);

  const paged = useMemo(() => {
    return filtered.slice(first, first + rowsPerPage);
  }, [filtered, first, rowsPerPage]);

  const openAdd = () => { setForm({ name: '', code: '', partner_id: '' }); setIsAddOpen(true); };
  const openEdit = (row) => {
    setSelected(row);
    setForm({ name: row.name, code: row.code, email: row.email && row.email !== 'N/A' ? row.email : '', partner_id: '' });
    setIsEditOpen(true);
  };
  const openView = (row) => {
    setSelected(row);
    // Build details directly from the row without another API call
    const details = {
      client_id: row.id,
      clientName: row.name,
      clientCode: row.code,
      clientEmail: row.email && row.email !== 'N/A' ? row.email : '',
      created_at: row.created_at || '',
    };
    setSelectedDetails(details);
    setIsViewOpen(true);
  };

  // const submitAdd = async () => {
  //   if (!form.name || !form.code) {
  //     setSnackbar({ open: true, success: false, message: 'Name and Code are required' });
  //     return;
  //   }
  //   try {
  //     setLoading(true);
  //     const payload = {
  //       client_details: { name: form.name, code: form.code, email: form.email || '', partner_id: form.partner_id || '' },
  //       user_id: getUserId(),
  //     };
  //     const res = await apiService.postData('add_client', payload);
  //     if (res?.status !== 'success') throw new Error(res?.message || 'Create failed');
  //     setSnackbar({ open: true, success: true, message: 'Client added successfully' });
  //     setIsAddOpen(false);
  //     await fetchClients();
  //   } catch (e) {
  //     setSnackbar({ open: true, success: false, message: e.message || 'Failed to add client' });
  //   } finally { setLoading(false); }
  // };

  const submitAdd = async () => {
  const trimmedName = form.name.trim().toLowerCase();
  const trimmedCode = form.code.trim().toLowerCase();

  if (!trimmedName || !trimmedCode) {
    setSnackbar({ open: true, success: false, message: 'Name and Code are required' });
    return;
  }

  
  const exists = clients.some(
    (c) => c.name.trim().toLowerCase() === trimmedName
  );

  if (exists) {
    setSnackbar({ open: true, success: false, message: 'This client already exists' });
    return;
  }

  try {
    setLoading(true);
    const payload = {
      client_details: { 
        name: form.name, 
        code: form.code, 
        email: form.email || '', 
        partner_id: form.partner_id || '' 
      },
      user_id: getUserId(),
    };
    const res = await apiService.postData('add_client', payload);
    if (res?.status !== 'success') throw new Error(res?.message || 'Create failed');

    setSnackbar({ open: true, success: true, message: 'Client added successfully' });
    setIsAddOpen(false);
    await fetchClients();
  } catch (e) {
    setSnackbar({ open: true, success: false, message: e.message || 'Failed to add client' });
  } finally {
    setLoading(false);
  }
};

  const submitEdit = async () => {
    try {
      setLoading(true);
      if (!selected) return setIsEditOpen(false);
      const values = { clientName: form.name, clientEmail: form.email || '', clientCode: form.code };
      const res = await apiService.putData(`add_client/${selected.id}`, { values, updated_by: getUserId() });
      if (res?.status !== 'success') throw new Error(res?.message || 'Update failed');
      setSnackbar({ open: true, success: true, message: 'Client updated successfully' });
      setIsEditOpen(false);
      await fetchClients();
    } catch (e) {
      setSnackbar({ open: true, success: false, message: e.message || 'Failed to update client' });
    } finally { setLoading(false); }
  };

  const confirmDelete = async (row) => {
  setDeleteModal({
    open: true,
    client: row,
    isDeleting: false,
    
  });
};

const handleDeleteConfirm = async () => {
  if (!deleteModal.client) return;

  setDeleteModal(prev => ({ ...prev, isDeleting: true }));

  try {
    const res = await apiService.deleteData(`add_client/${deleteModal.client.id}`);
    if (res?.status !== 'success') throw new Error(res?.message || 'Delete failed');

    await fetchClients();
    setSnackbar({ open: true, success: true, message: 'Client deleted successfully' });
  } catch (e) {
    setSnackbar({ open: true, success: false, message: e.message || 'Failed to delete client' });
  } finally {
    setDeleteModal({ open: false, client: null, isDeleting: false });
  }
};

const closeDeleteModal = () => {
  setDeleteModal({ open: false, client: null, isDeleting: false, reason: '' });
};

  const exportExcel = () => {
  const rows = filtered.map(c => {
    const row = {};
    if (visibleColumns.id) row['ID'] = c.id;
    if (visibleColumns.name) row['Name'] = c.name;
    if (visibleColumns.code) row['Code'] = c.code;
    if (visibleColumns.email) row['Email'] = c.email || '';
    if (visibleColumns.created_at) row['Created'] = formatDateYMD(c.created_at);
    return row;
  });

  const ws = XLSX.utils.json_to_sheet(rows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Clients');
  XLSX.writeFile(wb, 'clients.xlsx');
};

const exportPDF = () => {
  const doc = new jsPDF();

  // Build headers dynamically based on visible columns
  const headers = [];
  if (visibleColumns.id) headers.push('ID');
  if (visibleColumns.name) headers.push('Name');
  if (visibleColumns.code) headers.push('Code');
  if (visibleColumns.email) headers.push('Email');
  if (visibleColumns.created_at) headers.push('Created');

  const head = [headers];

  // Build body dynamically as well
  const body = filtered.map(c => {
    const row = [];
    if (visibleColumns.id) row.push(c.id);
    if (visibleColumns.name) row.push(c.name);
    if (visibleColumns.code) row.push(c.code);
    if (visibleColumns.email) row.push(c.email || '');
    if (visibleColumns.created_at) row.push(formatDateYMD(c.created_at));
    return row;
  });

  jspdfAutotable(doc, {
    head,
    body,
    styles: { fontSize: 8, cellPadding: 2 },
    headStyles: { fillColor: [255, 107, 53] },
  });

  doc.save('clients.pdf');
};


  const formatDateYMD = (value) => {
    if (!value) return '';
    try {
      const d = new Date(value);
      if (!isNaN(d.getTime())) return d.toISOString().slice(0,10);
    } catch(_) {}
    return String(value).slice(0,10);
  };

  const capitalizeFirst = (s) => {
    if (typeof s !== 'string' || s.length === 0) return s;
    return s.charAt(0).toUpperCase() + s.slice(1);
  };

  return (
    <div className="roles-page-container">{/* reuse Roles layout styles */}
      <Sidebar />
      <div className="roles-page-content">
        <Breadcrumbs customLabel="Clients" />
        <div style={{ background: '#fff', borderRadius: '8px', padding: '24px 28px', boxShadow: '0 1px 3px rgba(0, 0, 0, 0.06)', borderLeft: '4px solid #ff6b35', marginBottom: '24px' }}>
          <h1 style={{ fontSize: '24px', fontWeight: 700, color: '#212529', margin: 0, display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ width: '28px', height: '28px', background: 'linear-gradient(135deg, #ff6b35 0%, #ff8c61 100%)', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '16px' }}>📋</span>
            Clients
          </h1>
          {/* <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop:'12px', position:'relative' }}>
            <button className="add-role-button" onClick={openAdd} title="Click to add new client">Add Client</button>
            <button className="add-role-button" ref={exportRef} onClick={()=>setShowExportMenu(s=>!s)} title="Click to export clients as pdf or csv">Export</button>
            {showExportMenu && (
              <div style={{ position:'absolute', top:'100%', right:0, marginTop:6, background:'#fff', border:'1px solid #e5e7eb', borderRadius:6, boxShadow:'0 4px 10px rgba(0,0,0,0.08)', zIndex:10 }}>
                <button onClick={()=>{ exportExcel(); setShowExportMenu(false); }} style={{ display:'block', width:140, padding:'8px 12px', background:'transparent', border:'none', textAlign:'left', cursor:'pointer', color:'#4A4A4A' }}>Export CSV</button>
                <button onClick={()=>{ exportPDF(); setShowExportMenu(false); }} style={{ display:'block', width:140, padding:'8px 12px', background:'transparent', border:'none', textAlign:'left', cursor:'pointer', color:'#4A4A4A' }}>Export PDF</button>
              </div>
            )}
          </div> */}
        </div>

        <Loader isLoading={loading} message="Loading Clients..." />

        {!loading && (
          <>
  
            <div style={{ 
  display: 'flex', 
  alignItems: 'center', 
  justifyContent: 'space-between', 
  marginBottom: '16px', 
  gap: '12px',
  flexWrap: 'wrap' // Ensures responsiveness on small screens
}}>
  {/* Search Bar */}
  <div className="search-container" style={{ flex: '1 1 300px', position: 'relative' }}>
    <input
      type="text"
      placeholder="Search clients..."
      value={search}
      onChange={(e) => setSearch(e.target.value)}
      className="search-input"
      title="Search by client name, code, email or ID"
      style={{ width: '100%', paddingRight: '36px' }}
    />
    <svg 
      className="role-search-icon" 
      width="20" 
      height="20" 
      viewBox="0 0 24 24" 
      fill="none" 
      stroke="#666" 
      strokeWidth="2"
      style={{ position: 'absolute', top: '50%', transform: 'translateY(-50%)' }}
    >
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  </div>
<div style={{ position: 'relative', display: 'inline-block' }}>
  <button
    // className="add-role-button"
    onClick={() => setShowColumnOptions(s => !s)}
    title="Show/Hide Columns"
    style={{
                cursor: "pointer",
                padding: "4px 12px",
                borderRadius: "6px",
                background: "transparent",
                color: "#4A4A4A",
                border: "1px solid #ff6b35",
                fontSize: "16px",
                fontWeight: 500,
              }}
  >
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2">
      <path d="M3 3h18v18H3z" />
      <path d="M3 9h18M3 15h18" />
    </svg>
   ⚙ Column options
  </button>


  {showColumnOptions && (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.4)',
        backdropFilter: 'blur(6px)',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'flex-end',
        zIndex: 1000,
      }}
    >
      <div
        style={{
          width: '340px',
          background: '#fff',
          borderRadius: '8px',
          overflow: 'hidden',
          marginTop: '100px',
          marginRight: '20px',
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '10px 12px',
            fontWeight: 700,
            background: '#ff6b35',
            color: '#fff',
          }}
        >
          <div>Column Options</div>
          <button
            onClick={() => setShowColumnOptions(false)}
            aria-label="Close"
            style={{ background: 'transparent', border: 'none', fontSize: '25px', cursor: 'pointer', color: '#fff' }}
          >
            ×
          </button>
        </div>

        {/* Body – list of columns */}
        <div style={{ maxHeight: '50vh', overflowY: 'auto', padding: '10px 12px' }}>
          {[
            { key: 'id',          label: 'ID' },
            { key: 'name',       label: 'Client Name' },
            { key: 'code',       label: 'Client Code' },
            { key: 'email',      label: 'Client Email' },
            { key: 'created_at', label: 'Created Date' },
            { key: 'actions',    label: 'Action' },
          ].map(col => (
            <div
              key={col.key}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '8px 0',
                borderBottom: '1px solid #f3f4f6',
              }}
            >
              <span style={{ fontSize: '14px', color: '#111827' }}>
                {col.label}
                {mandatoryColumns.includes(col.key) && <span style={{ color: '#ff6b35' }}> *</span>}
              </span>

              <button
                onClick={() => toggleColumn(col.key)}
                disabled={mandatoryColumns.includes(col.key)}
                aria-label={tempVisibleColumns[col.key] ? `Hide ${col.label}` : `Show ${col.label}`}
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: mandatoryColumns.includes(col.key) ? 'not-allowed' : 'pointer',
                  opacity: mandatoryColumns.includes(col.key) ? 0.6 : 1,
                }}
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#000"
                  strokeWidth="2"
                >
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                  <circle cx="12" cy="12" r="3" />
                  {!tempVisibleColumns[col.key] && (
                    <line x1="2" y1="2" x2="18" y2="18" stroke="#000" strokeWidth="3" />
                  )}
                </svg>
              </button>
            </div>
          ))}
        </div>

        {/* Footer – Cancel / Save */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', padding: '10px 12px', borderTop: '1px solid #e5e7eb' }}>
          <button
            onClick={() => {
              setTempVisibleColumns(visibleColumns);   // reset staging
              setShowColumnOptions(false);
            }}
            style={{ background: 'transparent', color: '#ff6b35', border: '1px solid #ff6b35', padding: '8px 14px', borderRadius: '6px' }}
          >
            Cancel
          </button>

          <button
            onClick={() => {
              const newVis = { ...tempVisibleColumns };
              mandatoryColumns.forEach(c => (newVis[c] = true));
              setVisibleColumns(newVis);
              localStorage.setItem('clientTableVisibleColumns', JSON.stringify(newVis));
              setShowColumnOptions(false);
            }}
            style={{ background: '#ff6b35', color: '#fff', border: '1px solid #ff6b35', padding: '8px 14px', borderRadius: '6px' }}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  )}
</div>

  {/* Action Buttons (aligned to the right) */}
  <div style={{ display: 'flex', gap: '10px', position: 'relative' }}>
    <button 
      className="add-role-button" 
      onClick={openAdd} 
      title="Click To Add New Client"
    >
      Add Client
    </button>
    <button 
      className="add-role-button" 
      ref={exportRef} 
      onClick={() => setShowExportMenu(s => !s)} 
      title="Click To Export Clients As PDF Or CSV"
    >
      Export
    </button>

    {/* Export Dropdown */}
    {showExportMenu && (
      <div style={{
        position: 'absolute',
        top: '100%',
        right: 0,
        marginTop: 6,
        background: '#fff',
        border: '1px solid #e5e7eb',
        borderRadius: 6,
        boxShadow: '0 4px 10px rgba(0,0,0,0.08)',
        zIndex: 10,
        minWidth: 140
      }}>
        <button
          onClick={() => { exportExcel(); setShowExportMenu(false); }}
          style={{
            display: 'block',
            width: '100%',
            padding: '8px 12px',
            background: 'transparent',
            border: 'none',
            textAlign: 'left',
            cursor: 'pointer',
            color: '#4A4A4A',
            fontSize: '14px'
          }}
          onMouseOver={(e) => e.target.style.backgroundColor = '#f9f9f9'}
          onMouseOut={(e) => e.target.style.backgroundColor = 'transparent'}
        >
          Export CSV
        </button>
        <button
          onClick={() => { exportPDF(); setShowExportMenu(false); }}
          style={{
            display: 'block',
            width: '100%',
            padding: '8px 12px',
            background: 'transparent',
            border: 'none',
            textAlign: 'left',
            cursor: 'pointer',
            color: '#4A4A4A',
            fontSize: '14px'
          }}
          onMouseOver={(e) => e.target.style.backgroundColor = '#f9f9f9'}
          onMouseOut={(e) => e.target.style.backgroundColor = 'transparent'}
        >
          Export PDF
        </button>
      </div>
    )}
  </div>
</div>

            <table className="roles-table">
              {/* <thead>
                <tr>
                  <th title="ID">ID</th>
                  <th title="Client Name">Client Name</th>
                  <th title="Client Code">Client Code</th>
                  <th title="Client Email">Client Email</th>
                  <th title="Created Date">Created</th>
                  <th title="Action">Action</th>
                </tr>
              </thead>
              <tbody>
                {paged.length > 0 ? (
                  paged.map((c) => (
                    <tr key={c.id}>
                      <td>{c.id}</td>
                      <td>{c.name}</td>
                      <td>{c.code}</td>
                      <td>{c.email}</td>
                      <td>{formatDateYMD(c.created_at)}</td>
                      <td>
                        <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', alignItems:'center' }}>
                          <button className="add-role-button" title="View Client" onClick={()=>openView(c)} style={{ display:'inline-flex', alignItems:'center', justifyContent:'center', width:25, height:25, padding:0 }}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                              <circle cx="12" cy="12" r="3" />
                            </svg>
                          </button>
                          <button className="add-role-button" title="Edit Client" onClick={()=>openEdit(c)} style={{ display:'inline-flex', alignItems:'center', justifyContent:'center', width:25, height:25, padding:0 }}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M12 20h9" />
                              <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
                            </svg>
                          </button>
                          <button className="add-role-button" title="Delete Client" onClick={()=>confirmDelete(c)} style={{ display:'inline-flex', alignItems:'center', justifyContent:'center', width:25, height:25, padding:0 }}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="3 6 5 6 21 6" />
                              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                            </svg>
                          </button>
                          <button className="add-role-button" title={`add contract under ${c.name}`} onClick={()=>{ window.location.href = '/add-document'; }} style={{ display:'inline-flex', alignItems:'center', justifyContent:'center', width:25, height:25, padding:0 }}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <line x1="12" y1="5" x2="12" y2="19" />
                              <line x1="5" y1="12" x2="19" y2="12" />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr><td colSpan="6">No clients found.</td></tr>
                )}
              </tbody> */}
              <thead>
  <tr>
    {visibleColumns.id && <th title="ID">ID</th>}
    {visibleColumns.name && <th title="Client Name">Client Name</th>}
    {visibleColumns.code && <th title="Client Code">Client Code</th>}
    {visibleColumns.email && <th title="Client Email">Client Email</th>}
    {visibleColumns.created_at && <th title="Created Date">Created</th>}
    {visibleColumns.actions && <th title="Action">Action</th>}
  </tr>
</thead>
<tbody>
  {paged.length > 0 ? (
    paged.map((c) => (
      <tr key={c.id}>
        {visibleColumns.id && <td>{c.id}</td>}
        {visibleColumns.name && <td>{capitalizeFirst(c.name)}</td>}
        {visibleColumns.code && <td>{c.code}</td>}
        {visibleColumns.email && <td>{c.email}</td>}
        {visibleColumns.created_at && <td>{formatDateYMD(c.created_at)}</td>}
        {visibleColumns.actions && (
          <td>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', alignItems: 'center' }}>
              <button
                className="add-role-button"
                title="view client"
                onClick={() => openView(c)}
                style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 25, height: 25, padding: 0 }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
              </button>
              <button
                className="add-role-button"
                title="edit client"
                onClick={() => openEdit(c)}
                style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 25, height: 25, padding: 0 }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 20h9" />
                  <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
                </svg>
              </button>
              <button
  className="add-role-button"
  title="delete client"
  onClick={() => confirmDelete(c)}
  style={{ 
    display: 'inline-flex', 
    alignItems: 'center', 
    justifyContent: 'center', 
    width: 25, 
    height: 25, 
    padding: 0,
    background: '#ff6b35' 
  }}
>
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </svg>
</button>
              {/* <button
                className="add-role-button"
                title={`add contract under ${c.name}`}
                onClick={() => { window.location.href = '/add-document'; }}
                style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 25, height: 25, padding: 0 }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
              </button> */}
            </div>
          </td>
        )}
      </tr>
    ))
  ) : (
    <tr>
      <td colSpan="6">No clients found.</td>
    </tr>
  )}
</tbody>
            </table>
           {filtered.length > 0 && (
            <div style={{ marginTop: '1rem' }}>
              <Paginator
                first={first}
                rows={rowsPerPage}
                totalRecords={filtered.length}
                rowsPerPageOptions={[5,10,20,50]}
                onPageChange={(e) => {
                  setFirst(e.first);
                  setRowsPerPage(e.rows);
                  setCurrentPage(Math.floor(e.first / e.rows) + 1);
                }}
              />
            </div>
           )}
          </>
        )}

        {isAddOpen && (
          <div className="role-modal-overlay">
            <div className="role-modal-content">
              <h3>Add Client</h3>
              <div className="role-modal-form">
                <label>Client Name</label>
                <input value={form.name} onChange={(e)=>setForm({...form, name: e.target.value})} className="modal-input" />
                <label>Client Code</label>
                <input value={form.code} onChange={(e)=>setForm({...form, code: e.target.value})} className="modal-input" />
                <label>Client Email (optional)</label>
                <input value={form.email} onChange={(e)=>setForm({...form, email: e.target.value})} className="modal-input" />
                {/* <label>Partner (optional)</label>
                <select value={form.partner_id} onChange={(e)=>{ const pid = e.target.value; const p = partners.find(x=>String(x.id)===String(pid)); setForm({...form, partner_id: pid, email: p?.email || form.email}); }} className="modal-input">
                  <option value="">Select partner</option>
                  {partners.map(p => (
                    <option key={p.id} value={p.id}>{p.email || p.name}</option>
                  ))}
                </select> */}
                <label>Partner (optional)</label>
<div style={{ position: 'relative' }}>
  {/* Dropdown input */}
  <div
    onClick={() => setForm((prev) => ({ ...prev, showPartnerList: !prev.showPartnerList }))}
    style={{
      position: 'relative',
      cursor: 'pointer',
    }}
  >
    <input
      type="text"
      placeholder="Select or search partner..."
      value={form.partnerSearch || ''}
      readOnly
      className="modal-input"
      style={{ paddingRight: '30px', cursor: 'pointer' }}
    />
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="#666"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{
        position: 'absolute',
        right: '10px',
        top: '50%',
        transform: 'translateY(-50%)',
        pointerEvents: 'none',
      }}
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  </div>

  {/* Dropdown list */}
  {form.showPartnerList && (
    <div
      style={{
        position: 'absolute',
        top: '100%',
        left: 0,
        right: 0,
        background: '#fff',
        border: '1px solid #ccc',
        borderRadius: '6px',
        maxHeight: '180px',
        overflowY: 'auto',
        zIndex: 1000,
        marginTop: '4px',
      }}
    >
      {/* Search bar inside dropdown */}
      <input
        type="text"
        placeholder="Search partner..."
        value={form.partnerSearchInput || ''}
        onChange={(e) => {
          const q = e.target.value.toLowerCase();
          setForm((prev) => ({ ...prev, partnerSearchInput: q }));
        }}
        style={{
          width: '100%',
          padding: '8px 10px',
          border: 'none',
          borderBottom: '1px solid #eee',
          outline: 'none',
          color: '#ff6b35',    
        }}
      />

      {/* Filtered partner list */}
      {partners
        .filter(
          (p) =>
            (p.name || '').toLowerCase().includes(form.partnerSearchInput || '') ||
            (p.email || '').toLowerCase().includes(form.partnerSearchInput || '')
        )
        .map((p) => (
          <div
            key={p.id}
            onClick={() => {
              setForm({
                ...form,
                partner_id: p.id,
                partnerSearch: p.name || p.email || '',
                partnerSearchInput: '',
                email: p.email || form.email,
                showPartnerList: false,
              });
            }}
            style={{
              padding: '8px 10px',
              cursor: 'pointer',
              background:
                String(form.partner_id) === String(p.id)
                  ? '#ffe9e1'
                  : 'transparent',
              transition: 'background 0.2s ease',
            }}
            onMouseOver={(e) =>
              (e.currentTarget.style.backgroundColor = '#ff6b35')
            }
            onMouseOut={(e) =>
              (e.currentTarget.style.backgroundColor =
                String(form.partner_id) === String(p.id)
                  ? '#ffe9e1'
                  : 'transparent')
            }
          >
            <span
              style={{
                color:
                  String(form.partner_id) === String(p.id)
                    ? '#000'
                    : '#333',
              }}
            >
              {p.email || p.name}
            </span>
          </div>
        ))}
    </div>
  )}
</div>

              </div>
              <div className="role-modal-buttons">
                <button className="role-modal-button role-save-button" onClick={submitAdd}>Save</button>
                <button className="role-modal-button role-cancel-button" onClick={()=>setIsAddOpen(false)}>Cancel</button>
              </div>
            </div>
          </div>
        )}

        {isEditOpen && (
          <div className="modal-overlay" style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.4)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1300 }}>
            <div style={{ width:'520px', background:'#fff', borderRadius:'10px', boxShadow:'0 10px 25px rgba(0,0,0,0.15)', overflow:'hidden' }}>
              <div style={{ padding:'12px 16px', background:'#ff6b35', color:'#fff', fontWeight:700, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                <h3 style={{ margin:0 }}>Edit Client</h3>
                <button onClick={()=>setIsEditOpen(false)} style={{ background:'transparent', border:'none', color:'#fff', fontSize:22, cursor:'pointer' }}>×</button>
              </div>
              <div style={{ padding:'14px 16px' }}>
                <label className="contract_type-label">Client Name</label>
                <input value={form.name} onChange={(e)=>setForm({...form, name: e.target.value})} className="modal-input" />
                <label className="contract_type-label">Client Email</label>
                <input value={form.email} onChange={(e)=>setForm({...form, email: e.target.value})} className="modal-input" />
                <label className="contract_type-label">Client Code</label>
                <input value={form.code} onChange={(e)=>setForm({...form, code: e.target.value})} className="modal-input" />
              </div>
              <div style={{ display:'flex', justifyContent:'flex-end', gap:10, padding:'12px 16px', borderTop:'1px solid #eee' }}>
                <button className="modal-button cancel-button" onClick={()=>setIsEditOpen(false)} style={{ background:'transparent', color:'#ff6b35', border:'1px solid #ff6b35', padding:'8px 14px', borderRadius:'6px' }}>Close</button>
                <button className="modal-button save-button" onClick={submitEdit} style={{ background:'#ff6b35', color:'#fff', border:'1px solid #ff6b35', padding:'8px 14px', borderRadius:'6px' }}>Save</button>
              </div>
            </div>
          </div>
        )}

        {isViewOpen && selected && (
          <div className="modal-overlay" style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.4)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1300 }}>
            <div style={{ width:'560px', maxHeight:'85vh', background:'#fff', borderRadius:'10px', boxShadow:'0 10px 25px rgba(0,0,0,0.15)', overflow:'hidden', display:'flex', flexDirection:'column' }}>
              <div style={{ padding:'12px 16px', background:'#ff6b35', color:'#fff', fontWeight:700, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                <h3 style={{ margin:0 }}>Client Details</h3>
                <button onClick={()=>setIsViewOpen(false)} style={{ background:'transparent', border:'none', color:'#fff', fontSize:22, cursor:'pointer' }}>×</button>
              </div>
              <div style={{ padding:'14px 16px', overflow:'auto' }}>
                {selectedDetails ? (
                  <div style={{ display:'grid', gridTemplateColumns:'160px 1fr', rowGap:8, columnGap:12 }}>
                    {Object.entries(selectedDetails).map(([k,v]) => (
                      <React.Fragment key={k}>
                        <div style={{ color:'#666', fontSize:12 }}>{k}</div>
                        <div style={{ color:'#111', fontSize:14, wordBreak:'break-word' }}>{String(v)}</div>
                      </React.Fragment>
                    ))}
                  </div>
                ) : (
                  <div>Loading details...</div>
                )}
              </div>
              <div style={{ display:'flex', justifyContent:'flex-end', gap:10, padding:'12px 16px', borderTop:'1px solid #eee' }}>
                <button className="modal-button cancel-button" onClick={()=>{ setIsViewOpen(false); openEdit(selected); }} style={{ background:'#ff6b35', color:'#fff', border:'1px solid #ff6b35', padding:'8px 14px', borderRadius:'6px' }}>Edit</button>
                <button className="modal-button cancel-button" onClick={()=>setIsViewOpen(false)} style={{ background:'transparent', color:'#ff6b35', border:'1px solid #ff6b35', padding:'8px 14px', borderRadius:'6px' }}>Close</button>
              </div>
            </div>
          </div>
        )}
        {/* Delete Confirmation Modal */}
<DeleteConfirmationModal
  isOpen={deleteModal.open}
  onClose={closeDeleteModal}
  onConfirm={handleDeleteConfirm}
  clientName={deleteModal.client?.name || ''}
  isDeleting={deleteModal.isDeleting}
/>
        <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={()=>setSnackbar(s=>({...s, open:false}))} message={snackbar.message} sx={{ "& .MuiSnackbarContent-root": { backgroundColor: snackbar.success ? '#4caf50' : '#f44336' }, zIndex:1400 }} />
      </div>
    </div>
  );
};
const DeleteConfirmationModal = ({
  isOpen,
  onClose,
  onConfirm,
  clientName,
  isDeleting
}) => {
  if (!isOpen) return null;

  return (
    <div className="delete-project-overlay">
      <div className="delete-project-content">
        <div className="delete-project-header">
          <h3>Delete Client</h3>
          <button
            className="delete-project-close"
            onClick={onClose}
            disabled={isDeleting}
          >
            ×
          </button>
        </div>

        <div className="delete-project-body">
          <p className="delete-project-message">
            Are you sure you want to <strong>permanently delete</strong> the client:
          </p>
          <p style={{ 
            margin: '12px 0', 
            fontWeight: '600', 
            color: '#ff6b35',
            fontSize: '16px'
          }}>
            "{clientName}"
          </p>
          <p style={{ fontSize: '13px', color: '#ff6b35' }}>
            <strong>This action cannot be undone.</strong>
          </p>
        </div>

        <div className="delete-project-footer">
          <button
            className="delete-project-cancel"
            onClick={onClose}
            disabled={isDeleting}
          >
            Cancel
          </button>
          <button
            className="delete-project-delete"
            onClick={() => onConfirm()}  // No reason passed
            disabled={isDeleting}
          >
            {isDeleting ? (
              <>
                <CircularProgress size={16} sx={{ color: "#fff", mr: 1 }} />
                Deleting...
              </>
            ) : (
              "Delete"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
export default Clients;
